/**
 * /ptu-planner — Engine 26 · PTU / Provisioned-Throughput ROI Planner.
 *
 * The RI-optimizer play, one platform generation later: should you buy Azure
 * OpenAI PTUs / Bedrock provisioned throughput, or stay pay-as-you-go?
 */
import React, { useEffect, useState } from 'react';
import { Cpu, RefreshCw, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token') || ''}`, 'Content-Type': 'application/json' });

const glass = 'bg-slate-900/50 backdrop-blur-xl border border-white/10';
const MODELS = ['gpt-4o', 'gpt-4o-mini', 'claude-sonnet', 'claude-haiku', 'gemini-flash', 'mistral'];

const VERDICT_STYLES = {
  COMMIT: 'bg-emerald-500/10 border-emerald-400/50 text-emerald-300',
  STAY_PAYG: 'bg-sky-500/10 border-sky-400/50 text-sky-300',
  BORDERLINE: 'bg-amber-500/10 border-amber-400/50 text-amber-300',
};

const Num = ({ label, value, onChange, testId }) => (
  <label className="text-xs text-slate-400 flex flex-col gap-1">
    {label}
    <input type="number" value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-white/15 px-2 py-1.5 text-sm bg-slate-900 font-mono text-slate-100" data-testid={testId} />
  </label>
);

const PtuPlanner = () => {
  const [model, setModel] = useState('gpt-4o');
  const [inTokensM, setInTokensM] = useState(500);
  const [outTokensM, setOutTokensM] = useState(100);
  const [peakRatio, setPeakRatio] = useState(3);
  const [sim, setSim] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  const [scan, setScan] = useState(null);
  const [scanErr, setScanErr] = useState(false);

  const runSim = async () => {
    setSimLoading(true);
    try {
      const r = await fetch(`${API}/ptu-planner/simulate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model,
          monthly_input_tokens: Math.max(1, Math.round(Number(inTokensM) * 1e6)),
          monthly_output_tokens: Math.max(0, Math.round(Number(outTokensM) * 1e6)),
          peak_to_avg_ratio: Number(peakRatio) || 1,
        }),
      });
      if (r.ok) setSim(await r.json());
    } finally {
      setSimLoading(false);
    }
  };

  const fetchScan = async () => {
    try {
      const r = await fetch(`${API}/ptu-planner/scan?days=30`, { headers: auth() });
      if (!r.ok) throw new Error('unauthorized');
      setScan(await r.json());
      setScanErr(false);
    } catch (e) {
      setScanErr(true);
    }
  };

  useEffect(() => { runSim(); fetchScan(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="ptu-planner-page">
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8">
          <Badge className="bg-violet-500/10 border-violet-400/40 text-violet-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold mb-3" data-testid="ptu-badge">
            <Cpu className="w-3 h-3 mr-1.5 inline" /> Engine 26 · PTU ROI Planner · world-first
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight"><BrandTitle>Provisioned-Throughput ROI Planner</BrandTitle></h1>
          <p className="text-slate-300 mt-2 max-w-2xl">
            PTUs are the reserved instances of AI — and teams buy them blind, or stay pay-as-you-go paying
            <span className="text-violet-300 font-semibold"> 2× more</span>. Nobody does the commitment math. We do,
            deterministically, with every assumption disclosed.
          </p>
        </div>

        {/* Public simulator */}
        <Card className={`${glass} mb-10`}>
          <CardContent className="p-6">
            <h2 className="text-base font-bold text-white mb-4">Should you commit? Run your numbers</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <label className="text-xs text-slate-400 flex flex-col gap-1">
                Model
                <select value={model} onChange={(e) => setModel(e.target.value)} className="rounded-lg border border-white/15 px-2 py-1.5 text-sm bg-slate-900 text-slate-100" data-testid="ptu-model-select">
                  {MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <Num label="Input tokens / mo (millions)" value={inTokensM} onChange={setInTokensM} testId="ptu-input-in" />
              <Num label="Output tokens / mo (millions)" value={outTokensM} onChange={setOutTokensM} testId="ptu-input-out" />
              <Num label="Peak-to-avg ratio" value={peakRatio} onChange={setPeakRatio} testId="ptu-input-peak" />
              <div className="flex items-end">
                <button onClick={runSim} disabled={simLoading}
                  className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white px-4 py-1.5 text-sm font-semibold"
                  data-testid="ptu-simulate-btn">
                  {simLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Cpu className="w-3.5 h-3.5" />} Calculate
                </button>
              </div>
            </div>

            {sim && (
              <div data-testid="ptu-sim-result">
                <div className={`rounded-xl border px-5 py-4 mb-5 ${VERDICT_STYLES[sim.verdict]}`} data-testid="ptu-verdict">
                  <div className="text-lg font-black tracking-tight">{sim.verdict.replace('_', ' ')}</div>
                  <div className="text-sm opacity-90">{sim.verdict_reason}</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {[['PAYG / mo', `$${sim.payg_monthly_usd.toLocaleString()}`, 'text-white', 'ptu-stat-payg'],
                    [`${sim.ptu_needed} PTUs / mo`, `$${sim.ptu_monthly_usd.toLocaleString()}`, 'text-violet-300', 'ptu-stat-ptu'],
                    ['Monthly delta', `${sim.monthly_savings_usd >= 0 ? '+' : '−'}$${Math.abs(sim.monthly_savings_usd).toLocaleString()}`, sim.monthly_savings_usd >= 0 ? 'text-emerald-300' : 'text-rose-300', 'ptu-stat-savings'],
                    ['Utilization', `${sim.utilization_pct}%`, 'text-sky-300', 'ptu-stat-util'],
                  ].map(([label, value, color, tid]) => (
                    <div key={tid} className={`rounded-xl ${glass} p-4`} data-testid={tid}>
                      <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-1">{label}</div>
                      <div className={`text-2xl font-extrabold tabular-nums font-mono ${color}`}>{value}</div>
                    </div>
                  ))}
                </div>
                <details data-testid="ptu-assumptions">
                  <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-300">Every assumption, disclosed</summary>
                  <div className="mt-2 text-xs font-mono text-slate-400 space-y-0.5">
                    <div>usd_per_ptu_month = ${sim.assumptions.usd_per_ptu_month}</div>
                    <div>tpm_per_ptu = {sim.assumptions.tpm_per_ptu.toLocaleString()}</div>
                    <div>min_ptu = {sim.assumptions.min_ptu}</div>
                    <div>input ${sim.assumptions.input_rate_per_1m}/1M · output ${sim.assumptions.output_rate_per_1m}/1M</div>
                    <div className="text-slate-500 italic">{sim.assumptions.note}</div>
                  </div>
                </details>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Authed scan */}
        <h2 className="text-base font-bold text-white mb-4">Your live PTU recommendation (last 30 days)</h2>
        {scanErr ? (
          <div className={`rounded-xl ${glass} px-6 py-10 text-center text-slate-500`} data-testid="ptu-scan-unauth">
            <AlertCircle className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            Sign in with proxy traffic to get per-model commit recommendations from real usage.
          </div>
        ) : (
          <div className={`rounded-2xl ${glass} overflow-hidden`}>
            {(!scan?.rows || scan.rows.length === 0) ? (
              <div className="px-6 py-10 text-center text-slate-500" data-testid="ptu-scan-empty">
                <AlertCircle className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                No proxy traffic yet. Route calls through the Auto-Compiler Proxy and this fills itself.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="ptu-scan-table">
                <thead className="bg-slate-900/60 border-b border-white/10">
                  <tr>
                    <th className="text-left px-6 py-3 font-semibold text-slate-300">Model</th>
                    <th className="text-right px-3 py-3 font-semibold text-slate-300">PAYG $/mo</th>
                    <th className="text-right px-3 py-3 font-semibold text-slate-300">PTU $/mo</th>
                    <th className="text-right px-3 py-3 font-semibold text-slate-300">Delta</th>
                    <th className="text-right px-6 py-3 font-semibold text-slate-300">Verdict</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {scan.rows.map((r, i) => (
                    <tr key={r.model} className="hover:bg-slate-900/60" data-testid={`ptu-scan-row-${i}`}>
                      <td className="px-6 py-3 font-mono text-emerald-300">{r.model}</td>
                      <td className="px-3 py-3 text-right tabular-nums font-mono text-slate-200">${r.payg_monthly_usd.toLocaleString()}</td>
                      <td className="px-3 py-3 text-right tabular-nums font-mono text-slate-200">${r.ptu_monthly_usd.toLocaleString()}</td>
                      <td className={`px-3 py-3 text-right tabular-nums font-mono font-semibold ${r.monthly_savings_usd >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                        {r.monthly_savings_usd >= 0 ? '+' : '−'}${Math.abs(r.monthly_savings_usd).toLocaleString()}
                      </td>
                      <td className="px-6 py-3 text-right"><span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${VERDICT_STYLES[r.verdict]}`}>{r.verdict.replace('_', ' ')}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <Card className={`${glass} mt-10`}>
          <CardContent className="p-5">
            <h3 className="font-bold text-white mb-2">Why this is the 2026 version of the RI optimizer</h3>
            <p className="text-sm text-slate-200 leading-relaxed">
              A decade ago, companies like ProsperOps built businesses on one insight: cloud commitments are
              mispriced by inattention. AI capacity is repeating the pattern — Azure OpenAI PTUs, Bedrock provisioned
              throughput, OpenAI scale tiers — except this time the swing is bigger, because inference demand is
              spikier. Under-commit and you pay 2× on-demand rates. Over-commit and you burn reservations at 30%
              utilization. The math above is deterministic, assumption-disclosed, and re-runs on your real traffic daily.
            </p>
            <p className="text-xs text-slate-500 mt-2 italic">First-to-market: no FinOps or LLM-observability tool does AI capacity commitment math (Jun 2026).</p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PtuPlanner;
